import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@src/stories/elements/Button/Button";
import AnimatedPulse from "../../AnimatedPulse/AnimatedPulse";
import Blush from "../../Blush/Blush";
import { TimelineIndicator } from "../Components/TimelineIndicator";

interface DesktopTimelineLayoutProps {
    items: Array<{
        title: string;
        description: string;
        icon?: string;
    }>;
    rightSideContent: {
        title: string;
        description?: string;
        buttonText: string;
        buttonUrl: string;
        pulseSettings: any;
    };
    stickyScrollSettings: any;
    stickyScrollRef: React.RefObject<HTMLDivElement | null>;
}

export const DesktopTimelineLayout: React.FC<DesktopTimelineLayoutProps> = ({ 
    items, 
    rightSideContent, 
    stickyScrollSettings, 
    stickyScrollRef 
}) => {
    const [currentSection, setCurrentSection] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [debouncedSection, setDebouncedSection] = useState(0);
    const [debouncedProgress, setDebouncedProgress] = useState(0);
    
    const timelineContainerRef = useRef<HTMLDivElement>(null);

    // Debounce timeline updates to reduce flickering
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSection(currentSection);
            setDebouncedProgress(scrollProgress);
        }, 50); // Small delay to smooth updates

        return () => clearTimeout(timeoutId);
    }, [currentSection, scrollProgress]);

    // Scroll tracking for normal scroll behavior
    useEffect(() => {
        const handleScroll = () => {
            if (!timelineContainerRef.current) return;

            const container = timelineContainerRef.current;
            const containerTop = container.offsetTop;
            const containerHeight = container.scrollHeight;
            const scrollY = window.scrollY;
            const windowHeight = window.innerHeight;

            // Calculate which section is currently in view
            const timelineItems = container.querySelectorAll('.timeline-item-section');
            let activeIndex = 0;
            let progress = 0;

            timelineItems.forEach((item, index) => {
                const element = item as HTMLElement;
                const itemTop = element.offsetTop + containerTop;
                const itemHeight = element.offsetHeight;
                const itemCenter = itemTop + itemHeight / 2;

                if (scrollY + windowHeight / 2 >= itemCenter) {
                    activeIndex = index;
                    
                    // Calculate progress within the current section
                    const sectionStart = itemTop - windowHeight / 2;
                    const sectionEnd = itemTop + itemHeight - windowHeight / 2;
                    const sectionProgress = Math.max(0, Math.min(1, 
                        (scrollY - sectionStart) / (sectionEnd - sectionStart)
                    ));
                    progress = sectionProgress;
                }
            });

            setCurrentSection(activeIndex);
            setScrollProgress(progress);

            // Call original callback if it exists
            if (stickyScrollSettings.onSectionChange) {
                stickyScrollSettings.onSectionChange(activeIndex, progress);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial call

        return () => window.removeEventListener('scroll', handleScroll);
    }, [items, stickyScrollSettings]);

    // Enhanced sticky scroll settings to track progress
    const enhancedStickyScrollSettings = {
        ...stickyScrollSettings,
        duration: 1.2, // Reduce duration for faster transitions
        triggerPoint: 20, // Start sticky mode earlier
        smoothScroll: true,
        onSectionChange: (section, progress) => {
            // Debounce rapid updates to reduce flickering
            const clampedSection = Math.max(0, Math.min(items.length - 1, section));
            const clampedProgress = Math.max(0, Math.min(1, progress));
            
            setCurrentSection(clampedSection);
            setScrollProgress(clampedProgress);
            
            // Call original callback if it exists
            if (stickyScrollSettings.onSectionChange) {
                stickyScrollSettings.onSectionChange(clampedSection, clampedProgress);
            }
        }
    };

    return (
        <div className="desktop-timeline-layout relative">
            <div className="flex flex-row w-full min-h-screen relative">
                {/* Left Side: Scrollable Timeline Content */}
                <div className="w-1/2 flex-shrink-0 relative">
                    {/* Scrollable timeline items container */}
                    <div className="timeline-items-container ml-16 py-20 relative" ref={timelineContainerRef}>
                        {/* Timeline indicator - positioned within the timeline container */}
                        {/* <div className="absolute left-[-40px] top-0 bottom-0 z-30 hidden lg:flex items-center pointer-events-none">
                            <div className="sticky top-1/2 transform -translate-y-1/2">
                                <TimelineIndicator
                                    items={items}
                                    currentIndex={debouncedSection}
                                    scrollProgress={debouncedProgress}
                                    orientation="vertical"
                                    position="left"
                                    className="timeline-desktop-indicator"
                                    showProgress={true}
                                    dotSize="medium"
                                />
                            </div>
                        </div> */}

                        {items.map((item, index) => (
                            <div key={index} className="timeline-item-section mb-32 last:mb-0">
                                <div className="timeline-left-container flex items-center justify-center min-h-[80vh]">
                                    <div className="timeline-item-wrapper">
                                        <div className="timeline-item-content-wrapper relative">
                                            <Blush size={400} color="#EDD5C9" opacity={0.2}/>
                                            
                                            <motion.div 
                                                className="timeline-item-content flex flex-col relative z-10 max-w-md text-left"
                                                initial={{ opacity: 0, x: -20 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: false, amount: 0.3 }}
                                                transition={{ duration: 0.6, delay: 0.1 }}
                                            >
                                                {item.icon && (
                                                    <div className="mb-4">
                                                        <img
                                                            src={item.icon}
                                                            alt={item.title}
                                                            className="w-10 h-10"
                                                        />
                                                    </div>
                                                )}
                                                <h3
                                                    className="mb-4 text-[24px] font-semibold text-gray-900 leading-tight"
                                                    dangerouslySetInnerHTML={{ __html: item.title }}
                                                ></h3>
                                                <p className="text-[16px] leading-relaxed text-gray-700">{item.description}</p>
                                            </motion.div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Side: Sticky Content */}
                <div className="w-1/2 flex-shrink-0 relative">
                    <div className="sticky top-0 h-screen">
                        <div className="timeline-right-container h-full flex justify-center items-center">
                            <div className="right-content-wrapper relative p-10 max-w-[460px]">
                                {/* Background effects */}
                                <div className="blush-background absolute inset-0 z-[-1] pointer-events-none">
                                    <div className="absolute w-full h-full bg-gradient-radial from-[#f9f6f0]/40 via-[#f1ede4]/30 to-transparent opacity-70"></div>
                                    <div className="absolute w-[140%] h-[140%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                                                  bg-gradient-radial from-[#f1ede4]/30 via-[#e9e3d6]/20 to-transparent opacity-60"></div>
                                </div>
                                
                                <div className="pulse-container absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-full w-full">
                                    <AnimatedPulse settings={rightSideContent.pulseSettings} />
                                </div>
                                
                                <div className="content-container relative z-10 flex flex-col gap-8">
                                    <h2 className="text-[36px] font-bold text-gray-900 leading-tight">{rightSideContent.title}</h2>
                                    <div className="text-[16px] leading-relaxed text-gray-700">
                                        {rightSideContent.description && (
                                            <p dangerouslySetInnerHTML={{ __html: rightSideContent.description }} />
                                        )}
                                    </div>
                                    <div className="mt-4">
                                        <Button 
                                            style="primary" 
                                            icon="right-arrow-alt" 
                                            label={rightSideContent.buttonText} 
                                            reverse={true}
                                            link={rightSideContent.buttonUrl}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DesktopTimelineLayout;